import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { getDefaultValuesWindow } from '@pages/WindowsCreateOrEdit/components/helpers'
import { getTransformDataForReset } from '@pages/WindowsCreateOrEdit/components/helpers/getTransformDataForReset'
import { useHandlers } from '@pages/WindowsCreateOrEdit/hooks/useHandlers'
import { ArraySectionsType } from '@pages/WindowsCreateOrEdit/types'

import { useFetchAllSearchAssistantsQuery } from '@redux/api/searchAssistant.api'
import { useFetchAllWindowsQuery } from '@redux/api/windows.api'

import { useVariables } from '@hooks'
import { ENTITY_TYPES_REQUEST } from '@constants'
import { POSTWindowParams } from '@types'

export const useWindowsCreateOrEdit = () => {
  const [allWindows, setAllWindows] = useState<POSTWindowParams[]>([])

  const { data: searchAssistantsData, isFetching: isLoadingSearchAssistantsData } =
    useFetchAllSearchAssistantsQuery(
      {
        searchPattern: undefined,
      },
      {
        refetchOnMountOrArgChange: true,
      }
    )

  const { data: windows, isFetching: isLoadingWindows } = useFetchAllWindowsQuery(
    {},
    { refetchOnMountOrArgChange: true }
  )

  useEffect(() => {
    if (windows) {
      setAllWindows(windows)
    }
  }, [windows])

  const methods = useForm<ArraySectionsType>({
    defaultValues: getDefaultValuesWindow(),
    mode: 'onChange',
  })

  const {
    watch,
    reset,
    formState: { isDirty, dirtyFields, errors },
    handleSubmit,
    setValue,
  } = methods

  const watchInternalId = watch('internalId')
  const watchWindowTitle = watch('windowTitle')
  const watchFilterFor = watch('filterSection')
  const watchFilter = watch('filter')
  const watchSections = watch('sections')

  const { data, handlers, state } = useHandlers({
    reset,
    dirtyFields,
    watchInternalId,
    watchWindowTitle,
    watchFilterFor,
    watchFilter,
    watchSections,
    handleSubmit,
    isDirty,
  })

  const { windowById } = data
  const { currentTab } = state

  const windowVariables = useVariables({ id: data.id, type: ENTITY_TYPES_REQUEST.WINDOW })

  useEffect(() => {
    if (windowById) {
      reset(getTransformDataForReset(windowById))
    }
  }, [windowById])

  const autocompleteOptionsFilterSections = useMemo(() => {
    const sections = windowById?.sections

    const newOptions = sections
      ? sections.map((section, index) => ({
          id: section?.id || index,
          label: section?.title || `Section ${index + 1}`,
        }))
      : []

    const currentValue = methods.getValues('filterSection')
    if (currentValue && !newOptions.some(opt => opt.id === currentValue.id)) {
      setValue('filterSection', null)
    }

    return newOptions
  }, [windowById?.sections])

  const autocompleteOptionsFilterSA = useMemo(
    () => searchAssistantsData?.map(sa => ({ id: sa.id, label: sa.title })) || [],
    [searchAssistantsData]
  )

  return {
    state: {
      ...state,
      allWindows,
      errors,
    },
    data: {
      ...data,
      methods,
      autocompleteOptionsFilterSections,
      autocompleteOptionsFilterSA,
      isLoadingSearchAssistantsData,
      isLoadingWindows,
      isDirty,
      windowVariables,
    },
    handlers: {
      ...handlers,
    },
  }
}
